// Hücre yardımcı fonksiyonları

// Sütun adını sayıya çevir (A=0, B=1, AA=26, vb.)
export const columnToIndex = (col) => {
  let result = 0;
  for (let i = 0; i < col.length; i++) {
    result *= 26;
    result += col.charCodeAt(i) - 64;
  }
  return result - 1;
};

// Sayıyı sütun adına çevir (0=A, 1=B, 26=AA, vb.)
export const indexToColumn = (index) => {
  let result = '';
  while (index >= 0) {
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26) - 1;
  }
  return result;
};

// Hücre koordinatını string'e çevir (0,0 -> A1)
export const cellToAddress = (row, col) => {
  return `${indexToColumn(col)}${row + 1}`;
};

// String'den hücre koordinatını al (A1 -> 0,0)
export const addressToCell = (address) => {
  const match = address.match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;
  
  const col = columnToIndex(match[1]);
  const row = parseInt(match[2]) - 1;
  return { row, col };
};

// Aralık referansını parse et (A1:B5)
export const parseRange = (range) => {
  const [start, end] = range.split(':');
  const startCell = addressToCell(start);
  const endCell = addressToCell(end);
  
  if (!startCell || !endCell) return null;
  
  return {
    startRow: Math.min(startCell.row, endCell.row),
    endRow: Math.max(startCell.row, endCell.row),
    startCol: Math.min(startCell.col, endCell.col),
    endCol: Math.max(startCell.col, endCell.col)
  };
};

// Hücre değerinin tipini belirle
export const getCellType = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'empty';
  }
  
  if (typeof value === 'number' || !isNaN(parseFloat(value))) {
    return 'number';
  }
  
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  
  // Tarih kontrolü
  const date = new Date(value);
  if (!isNaN(date.getTime()) && value.toString().match(/^\d{4}-\d{2}-\d{2}/)) {
    return 'date';
  }
  
  return 'text';
};

// Sayısal değer formatla
export const formatNumber = (value, format = '') => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY'
      }).format(num);
    
    case 'percent':
      return `${(num * 100).toFixed(2)}%`;
    
    case 'decimal':
      return num.toFixed(2);
    
    case 'integer':
      return Math.round(num).toString();
    
    default:
      return num.toString();
  }
};

// Tarih formatla
export const formatDate = (value, format = '') => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  
  switch (format) {
    case 'short':
      return date.toLocaleDateString('tr-TR');
    
    case 'long':
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    
    case 'time':
      return date.toLocaleTimeString('tr-TR');
    
    case 'datetime':
      return date.toLocaleString('tr-TR');
    
    default:
      return date.toISOString().split('T')[0];
  }
};

// Hücre değerini görüntüleme formatında al
export const getDisplayValue = (cell) => {
  if (!cell) return '';
  
  const value = cell.value;
  const format = cell.format;
  
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  // Formül varsa hesaplanmış değeri göster
  if (cell.formula) {
    return value;
  }
  
  // Format tipine göre değeri formatla
  const cellType = getCellType(value);
  
  switch (cellType) {
    case 'number':
      return formatNumber(value, format.numberFormat);
    
    case 'date':
      return formatDate(value, format.dateFormat);
    
    default:
      return value.toString();
  }
};

// Hücre stilini hesapla
export const getCellStyle = (cell, isActive = false, isSelected = false) => {
  const baseStyle = {
    backgroundColor: cell?.format?.backgroundColor || '#ffffff',
    color: cell?.format?.textColor || '#000000',
    fontFamily: cell?.format?.fontFamily || 'Arial',
    fontSize: `${cell?.format?.fontSize || 11}px`,
    fontWeight: cell?.format?.bold ? 'bold' : 'normal',
    fontStyle: cell?.format?.italic ? 'italic' : 'normal',
    textDecoration: cell?.format?.underline ? 'underline' : 'none',
    textAlign: cell?.format?.horizontalAlign || 'left',
    verticalAlign: cell?.format?.verticalAlign || 'top'
  };
  
  // Aktif hücre stili
  if (isActive) {
    baseStyle.border = '2px solid #2196f3';
    baseStyle.backgroundColor = '#e3f2fd';
  }
  
  // Seçili hücre stili
  if (isSelected && !isActive) {
    baseStyle.backgroundColor = '#bbdefb';
    baseStyle.borderColor = '#1976d2';
  }
  
  return baseStyle;
};

// Hücre validasyonu
export const validateCellValue = (value, validation = {}) => {
  if (!validation.enabled) return { isValid: true };
  
  const errors = [];
  
  // Boş değer kontrolü
  if (validation.required && (value === null || value === undefined || value === '')) {
    errors.push('Bu alan zorunludur');
  }
  
  // Sayısal değer kontrolü
  if (validation.type === 'number') {
    const num = parseFloat(value);
    if (isNaN(num)) {
      errors.push('Sayısal değer giriniz');
    } else {
      if (validation.min !== undefined && num < validation.min) {
        errors.push(`Minimum değer: ${validation.min}`);
      }
      if (validation.max !== undefined && num > validation.max) {
        errors.push(`Maksimum değer: ${validation.max}`);
      }
    }
  }
  
  // Metin uzunluğu kontrolü
  if (validation.type === 'text' && validation.maxLength) {
    if (value.toString().length > validation.maxLength) {
      errors.push(`Maksimum ${validation.maxLength} karakter`);
    }
  }
  
  // Özel format kontrolü
  if (validation.pattern) {
    const regex = new RegExp(validation.pattern);
    if (!regex.test(value)) {
      errors.push('Geçersiz format');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
